/**
 * Preview Result page — stripped-down read-only view of a no-login
 * incident analysis. Renders ONLY the allowed surface area:
 *   - Domain agent summary (hypotheses)
 *   - Likely RCA hypothesis (root_cause_analysis or top hypothesis)
 *   - Recommended next actions (consensus.ranked_actions, top 3)
 *   - Stakeholder update draft (triage.summary / executive summary)
 *   - Human approval reminder
 *
 * Everything else from the war room (PDF export, share card, rerun,
 * dashboard navigation, billing widget) is intentionally absent.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Sparkles,
  ShieldCheck,
  Megaphone,
  Search,
  ListChecks,
  AlertCircle,
  LogIn,
  CheckCircle2,
} from "lucide-react";
import Header from "@/components/cre/Header";
import Footer from "@/components/cre/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  getOrCreateAnonymousTrialId,
  fetchPreviewIncident,
} from "@/lib/previewClient";

const STAGES_PROGRESS = ["analyzing", "synthesizing", "running"];

// -----------------------------
// Business-friendly stakeholder update — 3-5 short sentences. NEVER
// surfaces the triage classifier wording ("rule-based confidence …")
// or any confidence number. Built deterministically from the incident.
// -----------------------------
function _lcfirst(s) {
  if (!s) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
}

function _trimDot(s) {
  return (s || "").trim().replace(/\.+\s*$/, "");
}

// Map specialist domains to short, business-readable focus areas — used
// to build the "Teams are checking …" sentence without surfacing the
// agents' long technical hypotheses verbatim.
const DOMAIN_FOCUS = {
  application: "application logic",
  infrastructure: "infrastructure capacity",
  network: "network routing",
  database: "database performance",
  security: "security controls",
  service_impact: "service impact and dependencies",
  business: "business and revenue impact",
  observability: "telemetry and observability",
  data: "data integrity",
  release: "recent release changes",
};

function _domainFocus(domain) {
  if (!domain) return null;
  const key = String(domain).toLowerCase().replace(/-/g, "_");
  return DOMAIN_FOCUS[key] || key.replace(/_/g, " ");
}

function _joinList(parts) {
  if (!parts || !parts.length) return "";
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

function composeStakeholderDraft(incident) {
  if (!incident) return "";
  const sentences = [];
  const title = _trimDot(incident.title);
  const sys = (incident.affected_system || "").trim();
  const sev = (incident.severity || "").trim();
  const symptoms = (incident.symptoms || [])
    .map((s) => (typeof s === "string" ? s.trim() : ""))
    .filter(Boolean);

  // 1. What is happening + which service + severity
  if (title) {
    if (sys) {
      sentences.push(
        `${title} is affecting ${sys}${sev ? ` (severity ${sev})` : ""}.`
      );
    } else {
      sentences.push(`${title}${sev ? ` (severity ${sev})` : ""}.`);
    }
  }

  // 2. Customer/business impact framed from symptoms (no confidence
  //    scores, no classifier wording)
  if (symptoms.length) {
    const top = symptoms.slice(0, 3).map(_trimDot);
    sentences.push(`Customers may experience ${top.join(", ")}.`);
  }

  // 3. Investigation focus — use specialist DOMAINS of the top
  //    contributing hypotheses, NOT their long verbatim hypothesis
  //    text. Falls back to a synthesized RCA if no hypotheses are
  //    available, or to a generic line as a last resort.
  const hyps = incident.hypotheses || [];
  const domains = Array.from(
    new Set(
      [...hyps]
        .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))
        .map((h) => _domainFocus(h.domain))
        .filter(Boolean)
    )
  ).slice(0, 4);
  const rca = _trimDot(incident.root_cause_analysis?.most_probable_cause || "");
  if (domains.length) {
    sentences.push(`Teams are checking ${_joinList(domains)}.`);
  } else if (rca) {
    sentences.push(`Teams are investigating ${_lcfirst(rca)}.`);
  }

  // 4. Human approval reminder — always last
  sentences.push("Recommended actions require human approval before execution.");

  return sentences.slice(0, 5).join(" ");
}



export default function PreviewResult() {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const trialId = getOrCreateAnonymousTrialId();
  const [incident, setIncident] = useState(null);
  const [err, setErr] = useState(null);
  const [polling, setPolling] = useState(false);

  // Poll while status is analyzing.
  useEffect(() => {
    let mounted = true;
    let pollTimer = null;

    const tick = async () => {
      try {
        const inc = await fetchPreviewIncident(trialId, incidentId);
        if (!mounted) return;
        setIncident(inc);
        const done =
          inc.status === "awaiting_approval" ||
          inc.status === "approved" ||
          inc.status === "rejected" ||
          inc.status === "failed";
        if (!done) {
          pollTimer = setTimeout(tick, 4000);
          setPolling(true);
        } else {
          setPolling(false);
        }
      } catch (e) {
        if (!mounted) return;
        const status = e?.response?.status;
        if (status === 404) {
          setErr("This preview is no longer available. Start a new one.");
        } else {
          setErr("Couldn't load the preview.");
        }
        setPolling(false);
      }
    };

    tick();
    return () => {
      mounted = false;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [incidentId, trialId]);

  if (err) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
        <Header />
        <main className="flex-1 max-w-3xl w-full mx-auto px-5 md:px-8 py-12">
          <div
            data-testid="preview-error"
            className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-800"
          >
            {err}
          </div>
          <div className="mt-5 flex gap-2">
            <Link to="/preview"><Button variant="outline" size="sm">Try again</Button></Link>
            <Link to="/register">
              <Button size="sm" className="cre-cta-primary">Create free account</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
        <Header />
        <main className="flex-1 max-w-3xl w-full mx-auto px-5 md:px-8 py-12 flex items-center gap-3 text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading preview…
        </main>
        <Footer />
      </div>
    );
  }

  const analyzing = STAGES_PROGRESS.includes(incident.status);
  const hyps = incident.hypotheses || [];
  const ranked = incident.consensus?.ranked_actions || [];
  const topRanked = ranked.slice(0, 3);
  const likelyRca =
    incident.root_cause_analysis?.most_probable_cause ||
    (hyps.length
      ? [...hyps].sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))[0]?.hypothesis
      : null);
  const stakeholderDraft = composeStakeholderDraft(incident) ||
    "A draft will appear here once the agents complete their analysis.";

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
      <Header />
      <main
        data-testid="preview-result-page"
        className="flex-1 max-w-3xl w-full mx-auto px-5 md:px-8 pt-7 md:pt-10 pb-12 md:pb-16"
      >
        <Link
          to="/preview"
          data-testid="preview-result-back"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-sky-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
          Back to preview form
        </Link>

        <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-sky-200 bg-sky-50 text-sky-700 text-[11.5px] font-semibold">
          <Sparkles className="h-3 w-3" strokeWidth={2.25} />
          No-login preview
        </div>

        <h1 className="mt-2 font-heading font-black tracking-tight text-slate-900 text-2xl sm:text-3xl leading-[1.1]">
          {incident.title}
        </h1>
        <p className="mt-1 text-[12.5px] text-slate-500 font-mono">
          {incident.severity} · {incident.affected_system}
        </p>

        {/* Live status */}
        {analyzing ? (
          <div
            data-testid="preview-status-analyzing"
            className="mt-5 rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-2.5"
          >
            <Loader2 className="h-4 w-4 text-sky-600 animate-spin" />
            <span className="text-sm text-slate-700">
              Dispatching agents and synthesizing reasoning. This usually takes
              60–180 seconds.
            </span>
          </div>
        ) : (
          <div
            data-testid="preview-status-done"
            className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2.5"
          >
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <span className="text-sm text-emerald-800 font-semibold">
              Preview complete
            </span>
            <span className="text-[12px] text-emerald-700 ml-1">
              {hyps.length} agent hypotheses · {ranked.length} recommended actions
            </span>
          </div>
        )}

        {/* Domain agent summary */}
        <Section
          icon={Sparkles}
          title="Domain agent summary"
          testid="preview-section-agents"
        >
          {hyps.length === 0 ? (
            <p className="text-sm text-slate-500">
              {polling ? "Agents reasoning…" : "No agent hypotheses produced."}
            </p>
          ) : (
            <ul className="space-y-2.5">
              {hyps.map((h, i) => (
                <li
                  key={i}
                  data-testid={`preview-hypothesis-${i}`}
                  className="rounded-lg border border-slate-200 bg-white p-3"
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-[11.5px] font-mono uppercase tracking-wider text-slate-500">
                      {h.agent_name || h.domain}
                    </span>
                    {typeof h.confidence === "number" ? (
                      <span className="text-[11px] font-mono text-slate-500">
                        confidence {Math.round(h.confidence * 100)}%
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-sm text-slate-800 font-semibold">
                    {h.hypothesis}
                  </p>
                  {h.reasoning ? (
                    <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">
                      {h.reasoning}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Likely RCA hypothesis */}
        <Section icon={Search} title="Likely RCA hypothesis" testid="preview-section-rca">
          <p className="text-sm text-slate-800 leading-relaxed">
            {likelyRca || (polling ? "Synthesizing root cause…" : "RCA not produced.")}
          </p>
        </Section>

        {/* Recommended next actions */}
        <Section
          icon={ListChecks}
          title="Recommended next actions"
          testid="preview-section-actions"
        >
          {topRanked.length === 0 ? (
            <p className="text-sm text-slate-500">
              {polling ? "Ranking actions…" : "No ranked actions produced."}
            </p>
          ) : (
            <ol className="space-y-2">
              {topRanked.map((a, i) => (
                <li
                  key={i}
                  data-testid={`preview-action-${i}`}
                  className="rounded-lg border border-slate-200 bg-white p-3"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-sky-100 border border-sky-200 text-[11px] font-bold text-sky-700">
                      {i + 1}
                    </span>
                    <span className="text-sm text-slate-900 font-semibold">
                      {a.action}
                    </span>
                  </div>
                  {a.rationale ? (
                    <p className="mt-1.5 text-[12.5px] text-slate-600 leading-relaxed">
                      {a.rationale}
                    </p>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </Section>

        {/* Stakeholder update draft */}
        <Section
          icon={Megaphone}
          title="Stakeholder update draft"
          testid="preview-section-stakeholder"
        >
          <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">
            {stakeholderDraft}
          </p>
        </Section>

        {/* Human approval reminder */}
        <div
          data-testid="preview-approval-reminder"
          className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5 flex items-start gap-2.5"
        >
          <ShieldCheck className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" strokeWidth={2} />
          <p className="text-[13px] text-amber-900 leading-relaxed">
            <span className="font-semibold">Human approval required.</span>{" "}
            CRE outputs are advisory only. Validate every recommendation with
            a qualified operator before taking any action.
          </p>
        </div>

        {/* Strong sign-up CTA — always visible, more prominent when done */}
        <div
          data-testid="preview-upgrade-cta"
          className="mt-7 rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 via-white to-violet-50 px-5 py-5 sm:px-6 sm:py-6"
        >
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-xl bg-sky-100 border border-sky-200 flex items-center justify-center shrink-0">
              <LogIn className="h-4 w-4 text-sky-700" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-bold text-slate-900 text-base sm:text-lg">
                You are viewing a limited incident reasoning preview.
              </div>
              <p className="mt-1 text-[13px] sm:text-sm text-slate-700 leading-relaxed">
                Sign in free to unlock full agent reasoning, complete reports,
                PDF export, share cards, and approval-ready runbooks.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to="/register" data-testid="preview-cta-register">
                  <Button size="sm" className="cre-cta-primary rounded-lg font-semibold">
                    Create free account
                  </Button>
                </Link>
                <Link to="/login" data-testid="preview-cta-signin">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg border-sky-200 text-sky-700 hover:bg-sky-50 hover:text-sky-800 font-semibold"
                  >
                    Sign in
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ icon: Icon, title, testid, children }) {
  return (
    <section data-testid={testid} className="mt-6">
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
          <Icon className="h-3.5 w-3.5 text-slate-600" strokeWidth={1.75} />
        </div>
        <h2 className="font-heading font-bold tracking-tight text-slate-900 text-base">
          {title}
        </h2>
      </div>
      <div className="mt-2.5">{children}</div>
    </section>
  );
}
